/**
 * biomechanicsService.js — Biomechanics Analysis API Client
 * ==========================================================
 *
 * Frontend service for:
 *   - Model registry (best model, medley check, per-style)
 *   - Video analysis submission (biomechanics + race analysis)
 *   - Session tracking and results
 *
 * Endpoints consumed:
 *   GET  /vukz/biomechanics/models          — All models
 *   GET  /vukz/biomechanics/models/best     — Best (default) model
 *   GET  /vukz/biomechanics/models/medley   — Medley 4-style readiness
 *   GET  /vukz/biomechanics/models/:style   — Best model per style
 *   POST /vukz/biomechanics/analyze         — Submit video for analysis
 *   GET  /vukz/biomechanics/sessions/:id    — Get analysis results
 *   POST /vukz/race-analysis/analyze        — Submit race analysis
 *   GET  /vukz/race-analysis/races/:id      — Get race results
 *
 * @author Arena Stats / Paulo Brant
 * @date   2026-03-23
 */

import { AZURE } from './api';

// ═══════════════════════════════════════════════════════════════════
//  Model Registry
// ═══════════════════════════════════════════════════════════════════

/** Get all available pose models with capabilities and metrics */
export const getAvailableModels = () =>
  AZURE.get('/vukz/biomechanics/models');

/** Get the best (recommended) model — for UI header display */
export const getBestModel = () =>
  AZURE.get('/vukz/biomechanics/models/best');

/**
 * Medley readiness check — does the system have models for all 4 styles?
 * Returns: { medley_ready, single_model, per_style, missing_styles }
 */
export const checkMedleyReadiness = () =>
  AZURE.get('/vukz/biomechanics/models/medley');

/** Get best model for a specific style */
export const getModelForStyle = (style) =>
  AZURE.get(`/vukz/biomechanics/models/${style}`);

// ═══════════════════════════════════════════════════════════════════
//  Biomechanics Analysis
// ═══════════════════════════════════════════════════════════════════

/**
 * Submit a video for biomechanics analysis.
 *
 * @param {Object} params
 * @param {string} params.blobUrl       — Video blob URL (after upload)
 * @param {string} params.filename      — Original filename
 * @param {string} params.style         — freestyle|backstroke|butterfly|breaststroke
 * @param {number} [params.athleteId]   — Link to master_athletes
 * @param {string} [params.modelVersion] — Override model (default: best)
 * @param {number[]} [params.targetLanes] — Lanes to isolate (1-8)
 * @param {number} [params.nLanes]       — Total lanes (default 8)
 * @param {string} [params.analysisMode] — 'competition' (default) or 'training'
 * @param {number} [params.poolLengthM]  — Pool length in meters (default 50)
 * @param {number} [params.raceDistanceM] — Race distance in meters (default 50)
 * @param {number} [params.officialTimeS] — Official time in seconds
 * @returns {Promise} — { session_id, status }
 */
export const submitBiomechanicsAnalysis = ({
  blobUrl, filename, style = 'freestyle', athleteId,
  modelVersion, targetLanes, nLanes,
  analysisMode = 'competition', poolLengthM, raceDistanceM, officialTimeS,
}) =>
  AZURE.post('/vukz/biomechanics/analyze', {
    blob_url: blobUrl,
    filename,
    swimming_style: style,
    athlete_id: athleteId,
    model_version: modelVersion,
    target_lanes: targetLanes,
    n_lanes: nLanes,
    analysis_mode: analysisMode,
    pool_length_m: poolLengthM,
    race_distance_m: raceDistanceM,
    official_time_s: officialTimeS,
  });

/** Get biomechanics session results */
export const getBiomechanicsSession = (sessionId) =>
  AZURE.get(`/vukz/biomechanics/sessions/${sessionId}`);

/** Get session scores only */
export const getBiomechanicsScores = (sessionId) =>
  AZURE.get(`/vukz/biomechanics/sessions/${sessionId}/scores`);

/** Get session angles only */
export const getBiomechanicsAngles = (sessionId) =>
  AZURE.get(`/vukz/biomechanics/sessions/${sessionId}/angles`);

/** Get drill recommendations */
export const getBiomechanicsDrills = (sessionId) =>
  AZURE.get(`/vukz/biomechanics/sessions/${sessionId}/drills`);

/** Athlete progress over time */
export const getAthleteProgress = (athleteId) =>
  AZURE.get(`/vukz/biomechanics/athlete/${athleteId}/progress`);

// ═══════════════════════════════════════════════════════════════════
//  Race Analysis (Modelo Minas TC)
// ═══════════════════════════════════════════════════════════════════

/**
 * Submit a race for segment-based analysis.
 *
 * @param {Object} params
 * @param {string} params.blobUrl         — Video blob URL
 * @param {string} params.style           — Swimming style
 * @param {number} params.poolLength      — 25 or 50
 * @param {number} params.raceDistance    — 50, 100, 200, etc.
 * @param {number} [params.officialTime]  — Official time in seconds
 * @param {string} [params.athleteName]
 * @param {number} [params.athleteId]
 * @param {string} [params.competitionName]
 * @param {string} [params.competitionDate]
 * @param {number} [params.blockTime]     — Reaction time on block
 * @param {number[]} [params.targetLanes] — Lanes to isolate (1-8)
 * @param {number} [params.nLanes]        — Total lanes (default 8)
 * @returns {Promise} — { race_id, status, event_name }
 */
export const submitRaceAnalysis = ({
  blobUrl, style = 'freestyle', poolLength = 50, raceDistance = 50,
  officialTime, athleteName, athleteId, competitionName, competitionDate,
  blockTime, targetLanes, nLanes,
}) =>
  AZURE.post('/vukz/race-analysis/analyze', {
    blob_url: blobUrl,
    swimming_style: style,
    pool_length_m: poolLength,
    race_distance_m: raceDistance,
    official_time_s: officialTime,
    athlete_name: athleteName,
    athlete_id: athleteId,
    competition_name: competitionName,
    competition_date: competitionDate,
    block_time_s: blockTime,
    target_lanes: targetLanes,
    n_lanes: nLanes,
  });

/** Get full race report (segments, key times, pacing) */
export const getRaceReport = (raceId) =>
  AZURE.get(`/vukz/race-analysis/races/${raceId}`);

/** Get race segments only */
export const getRaceSegments = (raceId) =>
  AZURE.get(`/vukz/race-analysis/races/${raceId}/segments`);

/** Get race key times only */
export const getRaceKeyTimes = (raceId) =>
  AZURE.get(`/vukz/race-analysis/races/${raceId}/key-times`);

/** Compare athlete's races for same event */
export const compareAthleteRaces = (athleteId, { event, style, limit } = {}) =>
  AZURE.get(`/vukz/race-analysis/athlete/${athleteId}/compare`, {
    params: { event, style, limit },
  });

/** Segment benchmarks across athletes */
export const getRaceBenchmarks = ({ event, style, pool } = {}) =>
  AZURE.get('/vukz/race-analysis/benchmarks', {
    params: { event, style, pool },
  });

// ═══════════════════════════════════════════════════════════════════
//  Async Analysis (RECOMMENDED — Upload → Queue → Poll)
// ═══════════════════════════════════════════════════════════════════

/**
 * Upload video + enqueue for ASYNC biomechanics analysis.
 * Returns 202 immediately with session_id for polling — does NOT wait for inference.
 *
 * This is the recommended flow for production:
 *   1. uploadVideoAsync() → get session_id
 *   2. pollSessionUntilDone(session_id) → get results
 *
 * @param {File}    videoFile              — The video File object
 * @param {Object}  params
 * @param {string}  [params.style]         — freestyle|backstroke|butterfly|breaststroke
 * @param {number}  [params.poolLength]    — 25 or 50
 * @param {number}  [params.raceDistance]  — 50, 100, 200, etc.
 * @param {number}  [params.officialTime]  — Official time for calibration
 * @param {number[]} [params.targetLanes]  — Lanes to isolate
 * @param {number}  [params.maxFrames]     — Max frames (default 300)
 * @param {number}  [params.athleteId]     — Link to master_athletes
 * @param {Function} [params.onUploadProgress] — Axios progress callback (0-100%)
 * @returns {Promise<{data: {session_id: number, status: string, blob_url: string}}>}
 */
export const uploadVideoAsync = async (videoFile, {
  style = 'freestyle', poolLength = 50, raceDistance = 50,
  officialTime, targetLanes, maxFrames, athleteId, onUploadProgress,
  scanLanes = false, analysisMode = 'competition', strokeCounterVersion,
} = {}) => {
  // ── FAST PATH: Direct-to-Blob upload via SAS token ──
  // 1. Get SAS URL from API (instant)
  // 2. Upload directly to Azure Blob (frontend → blob, bypasses API server)
  // 3. Create session via lightweight JSON endpoint (instant)
  // This is 5-10x faster for large videos (100MB+).
  const _t0 = performance.now();
  try {
    // Step 1: Get SAS URL
    console.log('[SwimVision] Fast path: requesting SAS token...');
    const { data: sasResp } = await AZURE.get('/vukz/biomechanics/upload-sas', {
      params: { filename: videoFile.name },
      timeout: 15000,
    });
    console.log('[SwimVision] SAS token received in %dms', Math.round(performance.now() - _t0));

    if (sasResp?.success && sasResp.data?.sas_url) {
      // Step 2: Upload directly to blob via PUT with SAS
      const sasUrl = sasResp.data.sas_url;
      const blobUrl = sasResp.data.blob_url;
      const _t1 = performance.now();

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', sasUrl);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', videoFile.type || 'video/mp4');
        if (onUploadProgress) {
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) onUploadProgress({ loaded: e.loaded, total: e.total });
          };
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            console.log('[SwimVision] Blob PUT completed in %dms (%.1fMB)',
              Math.round(performance.now() - _t1), videoFile.size / (1024 * 1024));
            resolve();
          }
          else reject(new Error(`Blob upload failed: ${xhr.status} ${xhr.statusText}`));
        };
        xhr.onerror = () => reject(new Error(
          `Blob upload network error — likely CORS blocked. ` +
          `Check Azure Storage CORS allows PUT from ${window.location.origin}`
        ));
        xhr.ontimeout = () => reject(new Error(
          `Blob upload timeout after ${Math.round(xhr.timeout/1000)}s ` +
          `(${(videoFile.size / (1024*1024)).toFixed(1)}MB). ` +
          `If this persists, CORS may be blocking the request silently.`
        ));
        // Scale timeout by file size: 10min base + 1min per 100MB
        xhr.timeout = Math.max(600000, 600000 + Math.ceil(videoFile.size / (100 * 1024 * 1024)) * 60000);
        xhr.send(videoFile);
      });

      // Step 3: Create session (lightweight JSON, no file transfer)
      const { data: sessionResp } = await AZURE.post('/vukz/biomechanics/create-session', {
        blob_url: blobUrl,
        filename: videoFile.name,
        swimming_style: style,
        pool_length_m: poolLength,
        race_distance_m: raceDistance,
        official_time_s: officialTime || undefined,
        analysis_mode: analysisMode,
        scan_lanes: scanLanes,
        target_lanes: targetLanes || undefined,
        athlete_id: athleteId || undefined,
        stroke_counter_version: strokeCounterVersion || undefined,
      }, { timeout: 30000 });

      console.log('[SwimVision] ✅ Fast path total: %dms (session=%d)',
        Math.round(performance.now() - _t0), sessionResp?.data?.session_id);
      return { data: sessionResp };
    } else {
      console.warn('[SwimVision] SAS response missing sas_url, falling back:', sasResp);
    }
  } catch (sasErr) {
    console.warn('[SwimVision] Fast path failed (%dms), falling back to multipart: %s',
      Math.round(performance.now() - _t0), sasErr.message);
    console.error('[SwimVision] SAS error details:', {
      name: sasErr.name,
      status: sasErr.response?.status,
      data: sasErr.response?.data,
      videoName: videoFile?.name,
      videoSize: videoFile?.size,
    });
  }

  // ── FALLBACK: Original multipart upload through API server ──
  // ⚠️ This is 5-10x SLOWER — entire video goes through Flask
  console.warn('[SwimVision] ⚠️ Using SLOW multipart fallback (%.1fMB through API server)', videoFile.size / (1024 * 1024));
  const _tFallback = performance.now();
  const form = new FormData();
  form.append('video', videoFile);
  form.append('swimming_style', style);
  form.append('pool_length_m', String(poolLength));
  form.append('race_distance_m', String(raceDistance));
  form.append('analysis_mode', analysisMode);
  if (officialTime) form.append('official_time_s', String(officialTime));
  if (targetLanes?.length) form.append('target_lanes', targetLanes.join(','));
  if (maxFrames) form.append('max_frames', String(maxFrames));
  if (athleteId) form.append('athlete_id', String(athleteId));
  if (scanLanes) form.append('scan_lanes', 'true');

  return AZURE.post('/vukz/biomechanics/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 900000,  // 15min — large videos (800m ~1.6GB) need more time
    _skipRetry: true,
    onUploadProgress,
  });
};

/**
 * Poll session status until completed or failed.
 *
 * @param {number}   sessionId       — Session ID from uploadVideoAsync
 * @param {Object}   [opts]
 * @param {number}   [opts.interval] — Polling interval in ms (default 3000)
 * @param {number}   [opts.timeout]  — Max wait in ms (default 600000 = 10min)
 * @param {Function} [opts.onStatus] — Callback on each poll: ({ status, session_id, overall_score? })
 * @param {AbortSignal} [opts.signal] — AbortController signal to cancel polling
 * @returns {Promise<Object>} — Full session data when completed
 * @throws {Error} — If timeout, failed, cancelled, or aborted
 */
export const pollSessionUntilDone = async (sessionId, {
  interval = 3000,
  timeout = 900000,  // 15min — while on CPU; reduce after GPU migration
  onStatus,
  signal,
} = {}) => {
  const start = Date.now();

  while (Date.now() - start < timeout) {
    if (signal?.aborted) throw new Error('Analysis cancelled by user');

    try {
      const { data } = await AZURE.get(`/vukz/biomechanics/sessions/${sessionId}`);
      const session = data?.data;

      if (!session) throw new Error(`Session ${sessionId} not found`);

      if (onStatus) onStatus(session);

      if (session.status === 'completed') return session;
      if (session.status === 'failed') throw new Error(session.error_message || 'Analysis failed');
      if (session.status === 'cancelled') throw new Error('Analysis was cancelled');

      // Still pending or processing — wait and poll again
    } catch (err) {
      // If it's a definitive error (not a network hiccup), throw
      if (err.message?.includes('failed') || err.message?.includes('cancelled') || err.message?.includes('not found')) {
        throw err;
      }
      // Network error — log and continue polling
      console.warn(`[SwimVision] Poll error (will retry): ${err.message}`);
    }

    await new Promise((r) => setTimeout(r, interval));
  }

  // Instead of throwing, return a soft-timeout object.
  // The caller decides whether to show "still processing" or error.
  // This prevents the user from seeing a fatal error when the analysis
  // is actually still running on the worker.
  return {
    _timedOut: true,
    session_id: sessionId,
    status: 'processing',
    _elapsedMs: Date.now() - start,
  };
};

/**
 * Resume polling for a session that previously timed out.
 * Use this when the user returns to the page or clicks "check status".
 */
export const checkSessionStatus = async (sessionId) => {
  const { data } = await AZURE.get(`/vukz/biomechanics/sessions/${sessionId}`);
  return data?.data;
};

// ═══════════════════════════════════════════════════════════════════
//  Direct Analysis (Synchronous — Legacy, use for fallback only)
// ═══════════════════════════════════════════════════════════════════

/**
 * Run the FULL SwimXYZ pipeline synchronously on the server.
 * Downloads video from blob → Pose estimation → Biomechanics → Race Analysis
 *
 * This is the endpoint that returns REAL data, not mock.
 *
 * @param {Object} params
 * @param {string} params.blobUrl         — Video blob URL (after upload)
 * @param {string} [params.style]         — freestyle|backstroke|butterfly|breaststroke
 * @param {number} [params.poolLength]    — 25 or 50
 * @param {number} [params.raceDistance]  — 50, 100, 200, etc.
 * @param {number} [params.officialTime]  — Official time in seconds (calibration)
 * @param {number[]} [params.targetLanes] — Lanes to isolate (1-8)
 * @param {number} [params.nLanes]        — Total lanes (default 8)
 * @param {number} [params.maxFrames]     — Max frames (default 900)
 * @param {boolean} [params.includeRace]  — Include race analysis (default true)
 * @returns {Promise} — { biomechanics, race_analysis, sanity, pipeline }
 */
export const analyzeVideoDirect = ({
  blobUrl, style = 'freestyle', poolLength = 50, raceDistance = 50,
  officialTime, targetLanes, nLanes, maxFrames, includeRace = true,
}) =>
  AZURE.post('/vukz/biomechanics/direct/analyze', {
    blob_url: blobUrl,
    swimming_style: style,
    pool_length_m: poolLength,
    race_distance_m: raceDistance,
    official_time_s: officialTime,
    target_lanes: targetLanes,
    n_lanes: nLanes,
    max_frames: maxFrames,
    include_race: includeRace,
  }, {
    timeout: 600000,  // 10min — CPU inference on App Service is slow
    _skipRetry: true,  // Don't retry on timeout (inference is still running server-side)
  });

/**
 * Check if the direct analysis pipeline is ready (model loaded).
 * Use this to show/hide the "Analyze" button.
 */
export const checkDirectAnalysisHealth = () =>
  AZURE.get('/vukz/biomechanics/direct/health');

/**
 * Get the full list of capabilities — every metric the AI can compute.
 * Frontend uses this to decide what cards/tabs to display.
 */
export const getAnalysisCapabilities = () =>
  AZURE.get('/vukz/biomechanics/direct/capabilities');

/**
 * Upload a video file and run the FULL pipeline in one request.
 * This is the primary method for the VideoAnalysisPage.
 *
 * @param {File}    videoFile       — The video File object
 * @param {Object}  params
 * @param {string}  [params.style]         — freestyle|backstroke|butterfly|breaststroke
 * @param {number}  [params.poolLength]    — 25 or 50
 * @param {number}  [params.raceDistance]  — 50, 100, 200, etc.
 * @param {number}  [params.officialTime]  — Official time for calibration
 * @param {number[]} [params.targetLanes]  — Lanes to isolate
 * @param {number}  [params.maxFrames]     — Max frames (default 900)
 * @param {Function} [params.onUploadProgress] — Axios progress callback
 * @returns {Promise} — { biomechanics, race_analysis, sanity, pipeline }
 */
export const uploadAndAnalyze = (videoFile, {
  style = 'freestyle', poolLength = 50, raceDistance = 50,
  officialTime, targetLanes, maxFrames, onUploadProgress,
} = {}) => {
  const form = new FormData();
  form.append('video', videoFile);
  form.append('swimming_style', style);
  form.append('pool_length_m', String(poolLength));
  form.append('race_distance_m', String(raceDistance));
  if (officialTime) form.append('official_time_s', String(officialTime));
  if (targetLanes?.length) form.append('target_lanes', targetLanes.join(','));
  if (maxFrames) form.append('max_frames', String(maxFrames));

  return AZURE.post('/vukz/biomechanics/direct/upload-and-analyze', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,  // 10min — upload + CPU inference on App Service
    _skipRetry: true,  // Don't retry on timeout
    onUploadProgress,
  });
};

// ═══════════════════════════════════════════════════════════════════
//  Video Formats — Accepted types
// ═══════════════════════════════════════════════════════════════════

/**
 * Accepted video MIME types for the file input.
 * MTS/M2TS/MKV need browser-side conversion to MP4 before upload.
 */
export const ACCEPTED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',     // .mov
  'video/x-msvideo',     // .avi
  'video/webm',
  'video/x-matroska',    // .mkv
  'video/mp2t',          // .mts, .m2ts
  'video/mpeg',          // .mpg, .mpeg
];

/** File extensions (for display and input accept attribute) */
export const ACCEPTED_VIDEO_EXTENSIONS = [
  '.mp4', '.mov', '.avi', '.webm', '.mkv', '.mts', '.m2ts', '.mpg', '.mpeg',
];

/**
 * Check if a file needs conversion before analysis.
 * MP4 and MOV are directly supported. Others need FFmpeg conversion.
 */
export const needsConversion = (file) => {
  if (!file) return false;
  const name = file.name.toLowerCase();
  const directlySupported = ['.mp4', '.mov', '.webm'];
  return !directlySupported.some((ext) => name.endsWith(ext));
};


// ═══════════════════════════════════════════════════════════════════
//  Debug Overlay — Annotated Video + Timeline + Signal
// ═══════════════════════════════════════════════════════════════════

/**
 * Generate debug overlay artifacts for a session.
 *
 * @param {number} sessionId — Biomechanics session ID
 * @param {Object} [opts]
 * @param {number} [opts.lane] — Lane number (default 1)
 * @param {string} [opts.overlay_mode] — "clean" | "technical" | "forensic"
 * @param {boolean} [opts.drawSkeleton] — Draw skeleton overlay
 * @param {boolean} [opts.drawHud] — Draw HUD bar
 * @param {boolean} [opts.drawTimeline] — Draw timeline bar
 * @param {boolean} [opts.drawStrokes] — Draw stroke markers
 * @param {boolean} [opts.drawDistance] — Draw distance markers
 * @returns {Promise<{debug_id, status, timeline, signal, stats, video_url}>}
 */
// ─── DEBUG OVERLAY (protected by REACT_APP_ENABLE_DEBUG_SWIM_VISION) ───
const DEBUG_ENABLED = process.env.REACT_APP_ENABLE_DEBUG_SWIM_VISION === 'true';
const DEMO_BLOCKED_MSG = '[Swim Vision] Debug overlay disabled in production (REACT_APP_ENABLE_DEBUG_SWIM_VISION not set)';

export const generateDebugOverlay = async (sessionId, {
  lane = 1,
  overlay_mode = 'clean',
  drawSkeleton = true,
  drawHud = true,
  drawTimeline = true,
  drawStrokes = true,
  drawDistance = true,
} = {}) => {
  if (!DEBUG_ENABLED) { console.warn(DEMO_BLOCKED_MSG); return null; }
  const { data } = await AZURE.post('/vukz/swim-vision/debug/generate', {
    session_id: sessionId,
    lane,
    overlay_mode,
    draw_skeleton: drawSkeleton,
    draw_hud: drawHud,
    draw_timeline: drawTimeline,
    draw_strokes: drawStrokes,
    draw_distance: drawDistance,
  });
  return data;
};

/** Get debug overlay generation status */
export const getDebugOverlayStatus = (debugId) => {
  if (!DEBUG_ENABLED) { console.warn(DEMO_BLOCKED_MSG); return Promise.resolve({ data: null }); }
  return AZURE.get(`/vukz/swim-vision/debug/${debugId}/status`);
};

/** Get timeline events (strokes, distance markers) */
export const getDebugTimeline = async (debugId) => {
  if (!DEBUG_ENABLED) { console.warn(DEMO_BLOCKED_MSG); return null; }
  const { data } = await AZURE.get(`/vukz/swim-vision/debug/${debugId}/timeline`);
  return data?.data;
};

/** Get signal debug data (trajectory, peaks, troughs) */
export const getDebugSignal = async (debugId) => {
  if (!DEBUG_ENABLED) { console.warn(DEMO_BLOCKED_MSG); return null; }
  const { data } = await AZURE.get(`/vukz/swim-vision/debug/${debugId}/signal`);
  return data?.data;
};

/** Get annotated video URL — returns null if debug disabled */
export const getDebugVideoUrl = (debugId) => {
  if (!DEBUG_ENABLED) return null;
  return `/api/vukz/swim-vision/debug/${debugId}/video`;
};

/** Get thumbnail URL — returns null if debug disabled */
export const getDebugThumbnailUrl = (debugId) => {
  if (!DEBUG_ENABLED) return null;
  return `/api/vukz/swim-vision/debug/${debugId}/thumbnail`;
};

/** List all debug overlay sessions */
export const listDebugOverlays = async () => {
  if (!DEBUG_ENABLED) { console.warn(DEMO_BLOCKED_MSG); return []; }
  const { data } = await AZURE.get('/vukz/swim-vision/debug/list');
  return data?.data || [];
};


// ═══════════════════════════════════════════════════════════════════
//  Lane Scanning (Scene Understanding)
// ═══════════════════════════════════════════════════════════════════

/**
 * Poll lane scan results until scanning is complete.
 *
 * @param {number}   sessionId       — Session ID from uploadVideoAsync
 * @param {Object}   [opts]
 * @param {number}   [opts.interval] — Polling interval in ms (default 2000)
 * @param {number}   [opts.timeout]  — Max wait in ms (default 300000 = 5min)
 * @param {Function} [opts.onStatus] — Callback on each poll
 * @param {AbortSignal} [opts.signal] — AbortController signal to cancel
 * @returns {Promise<Object>} — Lane scan data when complete
 */
export const pollLaneScanUntilDone = async (sessionId, {
  interval = 2000,
  timeout = 300000,
  onStatus,
  signal,
} = {}) => {
  const start = Date.now();

  while (Date.now() - start < timeout) {
    if (signal?.aborted) throw new Error('Scan cancelled by user');

    try {
      const { data } = await AZURE.get(`/vukz/biomechanics/sessions/${sessionId}/lanes`);
      const result = data?.data;

      if (!result) throw new Error(`Session ${sessionId} not found`);
      if (onStatus) onStatus(result);

      if (result.status === 'scanned' && result.lanes_detected) {
        return result;
      }
      if (result.status === 'failed') {
        throw new Error('Lane scan failed');
      }
    } catch (err) {
      if (err.message?.includes('failed') || err.message?.includes('not found')) throw err;
      console.warn(`[SwimVision] Scan poll error (will retry): ${err.message}`);
    }

    await new Promise((r) => setTimeout(r, interval));
  }

  throw new Error(`Lane scan timed out after ${Math.round(timeout / 1000)}s`);
};


/**
 * Start analysis on specific lanes after scan.
 *
 * @param {number}   sessionId — Session ID
 * @param {number[]} lanes     — Lane numbers to analyze (e.g. [1, 3, 7])
 * @param {Object}   [opts]
 * @param {string}   [opts.style]       — Swimming style
 * @param {number}   [opts.raceDistance] — Race distance in meters
 * @returns {Promise<Object>}
 */
export const analyzeSelectedLanes = (sessionId, lanes, {
  style = 'freestyle', raceDistance = 50, poolLength = 50,
} = {}) =>
  AZURE.post(`/vukz/biomechanics/sessions/${sessionId}/analyze-lanes`, {
    lanes,
    swimming_style: style,
    race_distance_m: raceDistance,
    pool_length_m: poolLength,
  });


// ═══════════════════════════════════════════════════════════════════════════════
//  Dashboard + Race Context (Migration 187)
// ═══════════════════════════════════════════════════════════════════════════════

/** Dashboard stats — totals, styles, performance, recent sessions, daily trend */
export const getDashboardStats = () =>
  AZURE.get('/vukz/biomechanics/dashboard');

/**
 * Fetch lane/athlete assignments for a competition heat.
 * @param {number} competitionId
 * @param {number} eventId
 * @param {string} phase — e.g. "Final", "Semifinal"
 */
export const getRaceHeats = (competitionId, eventId, phase = 'Final') =>
  AZURE.get('/vukz/biomechanics/race-heats', {
    params: { competition_id: competitionId, event_id: eventId, phase },
  });

/** Get the deterministic race context linked to a session */
export const getRaceContext = (sessionId) =>
  AZURE.get(`/vukz/biomechanics/sessions/${sessionId}/race-context`);

/**
 * Link a session to a competition/event/phase + lanes (deterministic).
 * @param {number} sessionId
 * @param {Object} body — { competition_id, event_id, phase, lanes: [{lane_number, athlete_id, result_id}] }
 */
export const setRaceContext = (sessionId, body) =>
  AZURE.post(`/vukz/biomechanics/sessions/${sessionId}/race-context`, body);

/**
 * Cancel a pending or processing session.
 * @param {number} sessionId
 * @param {string} [reason] — Optional cancellation reason
 * @returns {Promise<{data: {session_id, previous_status, status}}>}
 */
export const cancelSession = (sessionId, reason) =>
  AZURE.post(`/vukz/biomechanics/sessions/${sessionId}/cancel`, { reason });

/**
 * Reprocess a failed/cancelled session — resets to pending for worker pickup.
 * @param {number} sessionId
 * @returns {Promise<{data: {session_id, previous_status, status}}>}
 */
export const reprocessSession = (sessionId) =>
  AZURE.post(`/vukz/biomechanics/sessions/${sessionId}/reprocess`);

// ═══════════════════════════════════════════════════════════════════
//  Annotations — Ground truth labels for ML training
// ═══════════════════════════════════════════════════════════════════

/** Get all annotations for a session */
export const getAnnotations = (sessionId) =>
  AZURE.get(`/vukz/biomechanics/sessions/${sessionId}/annotations`);

/** Save annotations (replaces all manual annotations for the session) */
export const saveAnnotations = (sessionId, annotations) =>
  AZURE.post(`/vukz/biomechanics/sessions/${sessionId}/annotations`, { annotations });

/** Delete a single annotation */
export const deleteAnnotation = (sessionId, annotationId) =>
  AZURE.delete(`/vukz/biomechanics/sessions/${sessionId}/annotations/${annotationId}`);

/** List all sessions with pagination */
export const listSessions = ({ athleteId, style, distance, status, limit = 20, offset = 0 } = {}) =>
  AZURE.get('/vukz/biomechanics/sessions', {
    params: { athlete_id: athleteId, style, distance, status, limit, offset },
  });


// ═══════════════════════════════════════════════════════════════════
//  Batch Analysis — Multi-video upload
// ═══════════════════════════════════════════════════════════════════

/**
 * Get a SAS URL for uploading a single file to blob storage.
 * Reusable helper for batch uploads (calls existing /upload-sas).
 */
export const getUploadSas = async (filename) => {
  const { data } = await AZURE.get('/vukz/biomechanics/upload-sas', {
    params: { filename },
    timeout: 15000,
  });
  return data;
};

/**
 * Upload a file directly to blob via SAS PUT.
 * @param {string} sasUrl — Signed URL for PUT
 * @param {File} file — File object
 * @param {Function} [onProgress] — Progress callback ({ loaded, total })
 * @returns {Promise<void>}
 */
export const uploadFileToBlob = (sasUrl, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sasUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress({ loaded: e.loaded, total: e.total });
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Blob upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Blob upload network error'));
    xhr.timeout = Math.max(600000, 600000 + Math.ceil(file.size / (100 * 1024 * 1024)) * 60000);
    xhr.ontimeout = () => reject(new Error('Blob upload timeout'));
    xhr.send(file);
  });

/**
 * Create a batch of sessions from already-uploaded blob URLs.
 * @param {Object} params
 * @param {Array} params.videos — [{ blob_url, filename, style, lane }]
 * @param {string} [params.strokeCounterVersion]
 * @param {number} [params.athleteId]
 * @returns {Promise<{ batch_id, session_ids }>}
 */
export const createBatch = async ({ videos, strokeCounterVersion, athleteId, competition_name, gender, race_distance_m }) => {
  const { data } = await AZURE.post('/vukz/biomechanics/batch', {
    videos,
    stroke_counter_version: strokeCounterVersion || undefined,
    athlete_id: athleteId || undefined,
    competition_name: competition_name || undefined,
    gender: gender || undefined,
    race_distance_m: race_distance_m || undefined,
  }, { timeout: 30000 });
  return data;
};

/**
 * Get aggregated batch status.
 * @param {string} batchId
 * @returns {Promise<{ batch_id, total, queued, processing, done, failed, sessions }>}
 */
export const getBatchStatus = async (batchId) => {
  const { data } = await AZURE.get(`/vukz/biomechanics/batch/${batchId}`);
  return data;
};

// ═══════════════════════════════════════════════════════════════════════════════
//  Video Clips — Human-in-the-loop Video Curation
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Upload video to Azure Blob only (no session creation, no analysis).
 * Used by the trimmer to upload the source before creating a clip.
 *
 * @param {File} videoFile
 * @param {Function} [onUploadProgress]
 * @returns {Promise<string>} — blob URL of uploaded video
 */
export const uploadVideoToBlob = async (videoFile, onUploadProgress) => {
  // Get SAS URL
  const { data: sasResp } = await AZURE.get('/vukz/biomechanics/upload-sas', {
    params: { filename: videoFile.name },
    timeout: 15000,
  });

  if (!sasResp?.success || !sasResp.data?.sas_url) {
    throw new Error('Failed to get SAS upload URL');
  }

  const sasUrl = sasResp.data.sas_url;
  const blobUrl = sasResp.data.blob_url;

  // Upload directly to Blob via PUT
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sasUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', videoFile.type || 'video/mp4');
    if (onUploadProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onUploadProgress({ loaded: e.loaded, total: e.total });
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Blob upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Blob upload network error'));
    xhr.timeout = Math.max(600000, 600000 + Math.ceil(videoFile.size / (100 * 1024 * 1024)) * 60000);
    xhr.send(videoFile);
  });

  return blobUrl;
};

/**
 * Create a trimmed video clip from a source video.
 * Backend runs FFmpeg to generate the clip and optionally starts analysis.
 *
 * @param {Object} params
 * @param {string} params.source_blob_url — Full blob URL of original video
 * @param {number} params.clip_start_ms — Start in ms
 * @param {number} params.clip_end_ms — End in ms
 * @param {string} [params.style] — freestyle, backstroke, etc.
 * @param {number} [params.distance_m]
 * @param {number} [params.lane]
 * @param {string} [params.purpose] — race_only, turn_analysis, etc.
 * @param {boolean} [params.auto_analyze=true] — auto-create session from clip
 * @returns {Promise<{ clip_id, status, duration_ms, auto_analyze }>}
 */
export const createVideoClip = async (params) => {
  const { data } = await AZURE.post('/vukz/biomechanics/clips', {
    auto_analyze: true,
    purpose: 'race_only',
    pool_length_m: 50,
    ...params,
  }, { timeout: 30000 });
  return data;
};

/**
 * Get clip status and metadata.
 * @param {number} clipId
 */
export const getClipStatus = async (clipId) => {
  const { data } = await AZURE.get(`/vukz/biomechanics/clips/${clipId}`);
  return data;
};

/**
 * Poll clip until it's ready or analyzed.
 * @param {number} clipId
 * @param {Object} [opts]
 * @param {number} [opts.interval=3000]
 * @param {number} [opts.timeout=120000]
 * @returns {Promise<Object>} — clip data when ready
 */
export const pollClipUntilReady = async (clipId, opts = {}) => {
  const interval = opts.interval || 3000;
  const timeout = opts.timeout || 120000;
  const start = Date.now();

  while (Date.now() - start < timeout) {
    const resp = await getClipStatus(clipId);
    const clip = resp?.data || resp;
    if (clip.status === 'ready' || clip.status === 'analyzed') return clip;
    if (clip.status === 'failed') throw new Error(clip.error_message || 'Clip generation failed');
    await new Promise(r => setTimeout(r, interval));
  }
  throw new Error('Clip generation timed out');
};
